/**
 * PWAInstallButton
 *
 * Listens for Chrome's `beforeinstallprompt` event, which fires only when:
 *   1. The page is served over HTTPS
 *   2. A service worker with a fetch handler is registered
 *   3. The manifest passes all Chrome installability checks
 *   4. The app is not already installed
 *
 * When the event fires, a small "Install App" button appears in the topbar.
 * Tapping it calls .prompt() on the deferred event, which opens Chrome's
 * native install dialog.
 *
 * If the button never appears, Chrome has not considered the app installable —
 * open chrome://flags on the device or DevTools → Application → Manifest to
 * see the exact reason.
 */

import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";

export default function PWAInstallButton({ compact = false }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled]           = useState(false);

  useEffect(() => {
    // Already running as installed PWA — nothing to show
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();          // Prevent Chrome's mini-infobar
      setDeferredPrompt(e);        // Store it so we can call .prompt() later
    };

    window.addEventListener("beforeinstallprompt", handler);

    // If the user completes or dismisses the install dialog
    window.addEventListener("appinstalled", () => {
      setInstalled(true);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  // Only render when Chrome has fired beforeinstallprompt and app isn't installed
  if (!deferredPrompt || installed) return null;

  if (compact) {
    // Icon-only button for tight topbars (mobile)
    return (
      <button
        onClick={handleInstall}
        aria-label="Install VianBills app"
        title="Install app"
        style={{
          width: 36, height: 36,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--flg-brand-500)",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <Download size={15} />
      </button>
    );
  }

  // Full button for desktop topbar
  return (
    <button
      onClick={handleInstall}
      aria-label="Install VianBills app"
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "0 12px",
        height: 34,
        background: "var(--flg-brand-500)",
        border: "none",
        borderRadius: 8,
        cursor: "pointer",
        color: "#fff",
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      <Download size={13} />
      Install App
    </button>
  );
}
